import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { useLoadDashboard } from "../hooks/dashboard/useLoadDashboard";
import { useEffect, useState } from "react";
import { VStack } from "@/components/layout/VStack";
import { MissingEnrollment } from "../components/dashboard/MissingEnrollment/MissingEnrollment";
import { Attendance } from "../components/dashboard/Attendance/Attendance";
import { Students } from "../components/dashboard/Students/Students";
import { ManageStudentSheet } from "../components/dashboard/ManageStudent/ManageStudentSheet";

export type MissingEnrollmentSession = {
  _id: string;
  name: string;
  singleton: boolean;
  missingEnrollments: any[];
  missingPreferences: any[];
};

export type MissingEnrollmentData = {
  _id: string;
  date: string;
  sessions: MissingEnrollmentSession[];
};

export type AttendanceRotations = {
  _id: string;
  date: string;
  records: number;
  present: number;
  sessions: {
    _id: string;
    name: string;
    records: number;
    present: number;
    absentStudents: {
      _id: string;
      name: string;
      gradYear: number;
    }[];
  }[];
};

export type AttendanceData = {
  totals: {
    records: number;
    present: number;
  };
  rotations: AttendanceRotations[];
};

export type StudentsData = {
  _id: string;
  name: string;
  email: string;
  gradYear: number;
  advisor?: string | null;
};

const AdminDashboard = (_props: { sysAdmin?: boolean } = {}) => {
  const [missingEnrollment, setMissingEnrollment] = useState<
    MissingEnrollmentData[] | null
  >(null);
  const [attendance, setAttendance] = useState<AttendanceData | null>(null);
  const [students, setStudents] = useState<StudentsData[] | null>(null);
  const [teachers, setTeachers] = useState<any[] | null>(null);
  const [sessionsData, setSessionsData] = useState<{
    rotations: any[];
    sessions: any[];
  } | null>(null);
  const [manageOpen, setManageOpen] = useState<{
    open: boolean;
    student: any;
  }>({
    open: false,
    student: undefined,
  });

  const {
    loadMissingEnrollment,
    loadAttendance,
    loadStudents,
    loadTeachers,
    loadSessionsData,
    loading,
  } = useLoadDashboard();

  useEffect(() => {
    loadMissingEnrollment(setMissingEnrollment);
    loadAttendance(setAttendance);
    loadStudents(setStudents);
  }, []);

  useEffect(() => {
    if (manageOpen.open && !sessionsData) {
      loadSessionsData(setSessionsData);
    }
  }, [manageOpen, sessionsData]);

  useEffect(() => {
    if (manageOpen.open && !teachers) {
      loadTeachers(setTeachers);
    }
  }, [manageOpen, teachers]);

  const onOpen = (studentId: string) => {
    const student = students?.find((s) => s._id === studentId);
    setManageOpen({ open: true, student });
  };

  return (
    <Dashboard activePage="/dashboard">
      <VStack>
        <h2
          style={{
            fontSize: "var(--text-2xl)",
            lineHeight: "var(--line-height-2xl)",
            fontWeight: "var(--font-bold)",
          }}
        >
          Admin Dashboard
        </h2>
        <p style={{ color: "var(--muted-foreground)" }}>
          View global stats and manage students.
        </p>
      </VStack>
      <MissingEnrollment
        data={missingEnrollment}
        loading={loading.missingEnrollments}
        onOpen={onOpen}
      />
      <Attendance
        data={attendance}
        loading={loading.attendance}
        onOpen={onOpen}
      />
      <Students data={students} loading={loading.students} onOpen={onOpen} />
      {manageOpen.student && (
        <ManageStudentSheet
          student={manageOpen.student}
          open={manageOpen.open}
          onOpenChange={(open) => {
            setManageOpen((prev) => ({
              open,
              student: prev.student,
            }));
          }}
          sessionsData={sessionsData}
          teachers={teachers}
          loading={loading.sessionsData}
          setMissingEnrollment={setMissingEnrollment}
          setAttendance={setAttendance}
        />
      )}
    </Dashboard>
  );
};

export default AdminDashboard;
